import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, CheckCircle2, ClipboardList, Plus } from 'lucide-react';
import {
  formatQty,
  type StockCount,
  type StockCountStatus,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { formatDate, formatDateTime } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ContextPanel, ReadField, StatTiles } from '@/components/ContextPanel';
import { StatusBadge, type BadgeKind } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Drawer, Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { DateInput } from '@/components/DateInput';
import { QtyInput } from '@/components/MoneyInput';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { useStockLocations } from '@/lib/queries';

const STATUS_TONE: Record<StockCountStatus, BadgeKind> = {
  ABERTO: 'warn',
  FECHADO: 'ok',
  CANCELADO: 'mute',
};

const STATUS_LABEL: Record<StockCountStatus, string> = {
  ABERTO: 'Em contagem',
  FECHADO: 'Fechado',
  CANCELADO: 'Cancelado',
};

/**
 * Inventário (contagem de estoque).
 *
 * O almoxarife abre a contagem de um local — o saldo esperado é congelado na
 * abertura — informa o que EXISTE na prateleira, e o fechamento gera um ajuste
 * por item divergente. Quem conta não fecha: fechar exige alçada.
 */
export function InventarioPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [opening, setOpening] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [counting, setCounting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const listQuery = useQuery({
    queryKey: ['stock-counts'],
    queryFn: () => api.get<{ data: StockCount[] }>('/stock-counts'),
  });

  const detailQuery = useQuery({
    queryKey: ['stock-count', selectedId],
    queryFn: () => api.get<StockCount>(`/stock-counts/${selectedId}`),
    enabled: Boolean(selectedId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['stock-counts'] });
    void queryClient.invalidateQueries({ queryKey: ['stock-count'] });
    void queryClient.invalidateQueries({ queryKey: ['stock-balances'] });
    void queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
    void queryClient.invalidateQueries({ queryKey: ['items'] });
  };

  const closeMutation = useMutation({
    mutationFn: (reason: string) =>
      api.post<{ count: StockCount; adjustments: number }>(
        `/stock-counts/${selectedId}/close`,
        { reason },
      ),
    onSuccess: () => {
      setClosing(false);
      refresh();
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (reason: string) =>
      api.post<StockCount>(`/stock-counts/${selectedId}/cancel`, { reason }),
    onSuccess: () => {
      setCancelling(false);
      refresh();
    },
  });

  const count = detailQuery.data;

  const columns: Array<Column<StockCount>> = [
    { key: 'code', label: 'Código', mono: true, strong: true, render: (row) => row.code },
    { key: 'locationName', label: 'Local', strong: true, render: (row) => row.locationName },
    { key: 'startedAt', label: 'Aberto em', mono: true, render: (row) => formatDate(row.startedAt) },
    {
      key: 'progress',
      label: 'Contados',
      right: true,
      mono: true,
      render: (row) => `${row.countedCount}/${row.items.length}`,
    },
    {
      key: 'divergences',
      label: 'Divergências',
      right: true,
      mono: true,
      render: (row) =>
        row.divergenceCount > 0 ? (
          <span style={{ color: T.amber, fontWeight: 600 }}>{row.divergenceCount}</span>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      key: 'status',
      label: 'Situação',
      render: (row) => <StatusBadge kind={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</StatusBadge>,
    },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Inventário"
        actions={
          can('inventarios:criar') ? (
            <Button variant="primary" icon={Plus} onClick={() => setOpening(true)}>
              Abrir inventário
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        {listQuery.isPending && <TableSkeleton columns={6} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Inventários de estoque"
            columns={columns}
            rows={listQuery.data.data}
            selectedId={selectedId}
            onRowClick={(row) => setSelectedId(row.id)}
            emptyState={
              <EmptyState
                title="Nenhum inventário ainda"
                description="Abra a contagem de um local: o saldo esperado é congelado na abertura."
                action={
                  can('inventarios:criar') ? (
                    <Button variant="primary" icon={Plus} onClick={() => setOpening(true)}>
                      Abrir inventário
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        )}
      </div>

      <ContextPanel
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        title={count ? `${count.code} · ${count.locationName}` : 'Carregando…'}
        subtitle={count ? STATUS_LABEL[count.status] : undefined}
        footer={
          count && count.status === 'ABERTO' ? (
            <>
              {can('inventarios:editar') && (
                <Button small variant="primary" icon={ClipboardList} onClick={() => setCounting(true)}>
                  Registrar contagem
                </Button>
              )}
              {can('inventarios:aprovar') && (
                <Button small icon={CheckCircle2} onClick={() => setClosing(true)}>
                  Fechar e ajustar
                </Button>
              )}
              {can('inventarios:excluir') && (
                <Button small variant="danger" icon={Ban} onClick={() => setCancelling(true)}>
                  Cancelar
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        {detailQuery.isPending && <p className="muted">Carregando…</p>}
        {count && (
          <>
            <StatTiles
              items={[
                { label: 'Itens contados', value: `${count.countedCount}/${count.items.length}` },
                { label: 'Divergências', value: String(count.divergenceCount) },
              ]}
            />
            <ReadField label="Aberto em" value={formatDateTime(count.startedAt)} mono />
            {count.closedAt && (
              <ReadField label="Fechado em" value={formatDateTime(count.closedAt)} mono />
            )}
            {count.notes && <ReadField label="Observações" value={count.notes} />}

            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="table" style={{ fontSize: 12.5 }}>
                <caption className="sr-only">Itens do inventário {count.code}</caption>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="is-right">Esperado</th>
                    <th className="is-right">Contado</th>
                    <th className="is-right">Diferença</th>
                  </tr>
                </thead>
                <tbody>
                  {count.items.map((item) => (
                    <tr key={item.id}>
                      <td className="is-strong">{item.itemName}</td>
                      <td className="is-right mono">{formatQty(item.expectedQtyMilli)}</td>
                      <td className="is-right mono">
                        {item.countedQtyMilli === null ? (
                          <span className="muted">—</span>
                        ) : (
                          formatQty(item.countedQtyMilli)
                        )}
                      </td>
                      <td
                        className="is-right mono"
                        style={{
                          color:
                            (item.differenceQtyMilli ?? 0) === 0
                              ? undefined
                              : (item.differenceQtyMilli ?? 0) > 0
                                ? T.green
                                : T.red,
                          fontWeight: (item.differenceQtyMilli ?? 0) === 0 ? undefined : 600,
                        }}
                      >
                        {item.differenceQtyMilli === null
                          ? '—'
                          : `${item.differenceQtyMilli > 0 ? '+' : ''}${formatQty(item.differenceQtyMilli)}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </ContextPanel>

      <OpenCountModal
        open={opening}
        onClose={() => setOpening(false)}
        onCreated={(id) => {
          setOpening(false);
          setSelectedId(id);
          refresh();
        }}
      />

      {count && (
        <CountSheetDrawer
          open={counting}
          count={count}
          onClose={() => setCounting(false)}
          onSaved={() => {
            setCounting(false);
            refresh();
          }}
        />
      )}

      <ConfirmDialog
        open={closing}
        requireReason
        loading={closeMutation.isPending}
        title="Fechar o inventário e ajustar o estoque?"
        confirmLabel="Fechar e ajustar"
        message={
          <>
            Cada item divergente vira um AJUSTE no livro de estoque, com origem
            "Inventário". O que não foi contado fica como está.
            {closeMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(closeMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setClosing(false);
          closeMutation.reset();
        }}
        onConfirm={(reason) => closeMutation.mutate(reason ?? '')}
      />

      <ConfirmDialog
        open={cancelling}
        destructive
        requireReason
        loading={cancelMutation.isPending}
        title="Cancelar este inventário?"
        confirmLabel="Cancelar inventário"
        message={
          <>
            A contagem é descartada e NENHUM ajuste é gerado — o saldo continua
            como está hoje.
            {cancelMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(cancelMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setCancelling(false);
          cancelMutation.reset();
        }}
        onConfirm={(reason) => cancelMutation.mutate(reason ?? '')}
      />
    </>
  );
}

function OpenCountModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const locations = useStockLocations();
  const [locationId, setLocationId] = useState('');
  const [notes, setNotes] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setLocationId('');
      setNotes('');
    }
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post<StockCount>('/stock-counts', body),
    onSuccess: (created) => onCreated(created.id),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Abrir inventário"
      description="O saldo esperado é congelado agora — o que mudar depois aparece como divergência"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={!locationId}
            onClick={() => mutation.mutate({ locationId, notes: notes.trim() || null })}
          >
            Abrir contagem
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}
      <FormSection>
        <Field label="Local a contar" htmlFor="inv-location">
          <select
            id="inv-location"
            className="input"
            value={locationId}
            onChange={(event) => setLocationId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {(locations.data ?? []).map((location) => (
              <option key={location.id} value={location.id}>
                {location.code} · {location.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Observações" htmlFor="inv-notes">
          <input
            id="inv-notes"
            className="input"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Contagem mensal do almoxarifado"
          />
        </Field>
      </FormSection>
    </Modal>
  );
}

function CountSheetDrawer({
  open,
  count,
  onClose,
  onSaved,
}: {
  open: boolean;
  count: StockCount;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [lines, setLines] = useState<Record<string, number | null>>({});
  /** Lote da SOBRA, por item — só o item que controla lote usa. */
  const [lotes, setLotes] = useState<Record<string, { code: string; expiry: string }>>({});
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const formKey = open ? count.id : null;
  if (formKey !== loadedFor) {
    setLoadedFor(formKey);
    setLines(
      Object.fromEntries(count.items.map((item) => [item.itemId, item.countedQtyMilli])),
    );
    setLotes(
      Object.fromEntries(
        count.items.map((item) => [
          item.itemId,
          { code: item.batchCode ?? '', expiry: item.batchExpiry ?? '' },
        ]),
      ),
    );
  }

  /** Só a SOBRA precisa de lote: o que faltou sai pelo que vence primeiro. */
  const sobraSemLote = (itemId: string, contado: number | null | undefined) => {
    const item = count.items.find((linha) => linha.itemId === itemId);
    if (!item?.tracksBatch || contado === null || contado === undefined) return false;
    return contado > item.expectedQtyMilli && !(lotes[itemId]?.code ?? '').trim();
  };

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post<StockCount>(`/stock-counts/${count.id}/fill`, body),
    onSuccess: onSaved,
  });

  const filled = Object.entries(lines).filter(([, value]) => value !== null);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Contagem · ${count.locationName}`}
      description="Informe o que EXISTE na prateleira — não a diferença"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Fechar
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={
              filled.length === 0 ||
              filled.some(([itemId, contado]) => sobraSemLote(itemId, contado))
            }
            onClick={() =>
              mutation.mutate({
                items: filled.map(([itemId, countedQtyMilli]) => ({
                  itemId,
                  countedQtyMilli: countedQtyMilli ?? 0,
                  batchCode: lotes[itemId]?.code.trim() || null,
                  batchExpiry: lotes[itemId]?.expiry || null,
                })),
              })
            }
          >
            Salvar contagem ({filled.length})
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        {count.items.length === 0 && (
          <p className="muted" style={{ fontSize: 13 }}>
            Este local não tinha saldo na abertura. Feche o inventário ou movimente algo primeiro.
          </p>
        )}
        {count.items.map((item) => {
          const counted = lines[item.itemId];
          const diff = counted === null || counted === undefined ? null : counted - item.expectedQtyMilli;
          return (
            <div key={item.id} className="card" style={{ padding: 12, marginBottom: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{item.itemName}</div>
              <div className="mono" style={{ fontSize: 12, color: T.inkFaint, marginTop: 2 }}>
                {item.sku} · esperado {formatQty(item.expectedQtyMilli)} {item.unit}
              </div>
              <div style={{ marginTop: 8, maxWidth: 220 }}>
                <QtyInput
                  unit={item.unit}
                  value={counted ?? null}
                  onChange={(value) => setLines((current) => ({ ...current, [item.itemId]: value }))}
                />
              </div>
              {diff !== null && diff !== 0 && (
                <p
                  className="mono"
                  style={{ fontSize: 12, marginTop: 6, color: diff > 0 ? T.green : T.red }}
                >
                  {diff > 0 ? 'Sobra' : 'Falta'} de {formatQty(Math.abs(diff))} {item.unit}
                </p>
              )}

              {item.tracksBatch && diff !== null && diff > 0 && (
                <div className="grid-2" style={{ marginTop: 8 }}>
                  <Field
                    label="Lote da sobra"
                    htmlFor={`inv-lote-${item.id}`}
                    hint="Material que apareceu tem de dizer de qual lote é"
                  >
                    <input
                      id={`inv-lote-${item.id}`}
                      className="input input--mono"
                      value={lotes[item.itemId]?.code ?? ''}
                      onChange={(event) =>
                        setLotes((atual) => ({
                          ...atual,
                          [item.itemId]: {
                            expiry: atual[item.itemId]?.expiry ?? '',
                            code: event.target.value,
                          },
                        }))
                      }
                      placeholder="L2026A"
                    />
                  </Field>
                  <Field label="Validade" htmlFor={`inv-val-${item.id}`}>
                    <DateInput
                      id={`inv-val-${item.id}`}
                      className="input input--mono"
                      value={lotes[item.itemId]?.expiry ?? ''}
                      onChange={(iso) =>
                        setLotes((atual) => ({
                          ...atual,
                          [item.itemId]: { code: atual[item.itemId]?.code ?? '', expiry: iso },
                        }))
                      }
                    />
                  </Field>
                </div>
              )}
            </div>
          );
        })}
      </FormSection>
    </Drawer>
  );
}
